<script setup lang="ts">
type Props = {
  forceShowLoading?: boolean;
  forceShowError?: boolean;
  isNotificationPage?: boolean;
};

const {
  forceShowLoading,
  isNotificationPage = false,
  forceShowError = false,
} = defineProps<Props>();

const { isGamificationActived, isNotificationActived } =
  storeToRefs(useSmarticoConfigs());

const { initSmarticoError, isReadyGamefication, isReadySmartico } =
  storeToRefs(useSmarticoInit());

const { isAuthenticated } = storeToRefs(useAuthStore());
const { isReadyMessages } = storeToRefs(useSmarticoMessages());
const { openLogin } = useAuth();

const showLoading = ref(true);

const router = useRouter();

const updatePage = (e: any) => {
  e.preventDefault();
  e.stopPropagation();
  router.go(0);
};

const isGamificationOrNotificationActived = computed(() => {
  if (isGamificationActived.value) {
    return true;
  }

  if (isNotificationActived.value && isNotificationPage) {
    return true;
  }

  return false;
});

const isGamificationOrNotificationReady = computed(() => {
  if (!isReadySmartico.value) {
    return false;
  }

  if (isReadyGamefication.value && !isNotificationPage) {
    return true;
  }

  if (isReadyMessages.value && isNotificationPage) {
    return true;
  }

  return false;
});

onMounted(() => {
  if (
    !isAuthenticated.value ||
    !isGamificationOrNotificationActived.value ||
    initSmarticoError.value ||
    isGamificationOrNotificationReady.value
  ) {
    showLoading.value = false;
  }

  if (!isAuthenticated.value && isGamificationOrNotificationActived.value) {
    setTimeout(() => {
      openLogin();
    }, 100);
  }
});

watch(isGamificationOrNotificationReady, (val) => {
  if (val === true) {
    setTimeout(() => {
      showLoading.value = false;
    }, 300);
  }
});
</script>

<template>
  <div
    v-if="!isGamificationOrNotificationActived || forceShowError"
    class="py-1 md:py-10 items-start"
    :class="$style.centerPage"
  >
    <BaseErrorMessage
      image-type="error"
      :message="$t('error.page_not_found.message')"
      :subtitle="$t('error.page_not_found.subtitle')"
      :title="$t('error.ops')"
    >
      <button @click="$router.push(localeLink('/'))">
        {{ $t('error.page_not_found.button') }}
      </button>
    </BaseErrorMessage>
  </div>

  <div
    v-else-if="!isAuthenticated"
    class="py-1 md:py-10 items-start"
    :class="$style.centerPage"
  >
    <BaseErrorMessage
      :message="$t('error.unauthorized.message')"
      :subtitle="$t('error.unauthorized.subtitle')"
      :title="$t('error.ops')"
    >
      <button @click="openLogin()">
        <NuxtIcon name="sign_in" />
        {{ $t('error.unauthorized.button') }}
      </button>
    </BaseErrorMessage>
  </div>

  <div
    v-else-if="initSmarticoError"
    class="py-1 md:py-10 items-start"
    :class="$style.centerPage"
  >
    <BaseErrorMessage
      :class="$style.customMessage"
      :message="$t('error.reload.message')"
      :style="'error'"
      :subtitle="$t('error.reload.subtitle')"
      :title="$t('error.ops')"
    >
      <button
        :class="$style.error"
        @click="updatePage"
      >
        <NuxtIcon name="sync" />
        {{ $t('error.reload.button') }}
      </button>
    </BaseErrorMessage>
  </div>

  <template v-else-if="showLoading || forceShowLoading">
    <template v-if="isNotificationPage">
      <div :class="$style.gridNotificationItems">
        <div
          v-for="(n, index) in 3"
          :key="`skeleton-${index}`"
          class="defaultItemBox notHover"
        >
          <div :class="$style.loadingItem">
            <div
              v-for="(c, i) in 3"
              :key="`sk-${i}`"
              class="load skeleton-loading"
              :class="$style.skeleton"
            ></div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div :class="[$style.centerPage, $style.loading]">
        <BaseCommonLogo
          always-icon
          :class="[$style.iconLoading, $style.pulse]"
          custom-size
        />
      </div>
    </template>
  </template>
</template>

<style module lang="postcss">
.centerPage {
  @apply w-full min-h-[500px] flex items-center justify-center;

  &.loading {
    @apply absolute top-0 left-0 right-0 bottom-0;
  }

  .customMessage {
    button {
      &.error {
        @apply bg-error text-white;
      }
    }
  }

  .iconLoading {
    @apply h-8 md:h-10 w-[auto];
  }

  .pulse {
    animation: pulse 1s infinite;
  }
}

.gridNotificationItems {
  @apply flex flex-col gap-3 md:gap-4 items-stretch content-stretch w-full mb-auto;
}

.loadingItem {
  @apply flex gap-3 flex-wrap p-3;
  .skeleton {
    @apply invert opacity-10 w-full;
    &:nth-child(1) {
      @apply h-[25px];
    }
    &:nth-child(2) {
      @apply h-[60px];
    }
    &:nth-child(3) {
      @apply h-[20px];
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.1);
    opacity: 0.5;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
